<template>
  <div class="el-main">
    <!-- プロフィール補足カード（Website / Recent Position） -->
    <!-- 氏名・電話・Email・生年月日・年齢は BasicInfo.vue の en-profile カードで管理 -->
    <div class="card" v-show="currentCardId == 'en-profile'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>ウェブサイト・役職</h2>
          <p>PDFヘッダーに表示される補足情報を入力してください（任意）</p>
        </div>
        <div class="card-content">
          <div class="edit-form">
            <div class="edit-form-item">
              <div class="item-name">ウェブサイト</div>
              <div class="item-body">
                <el-input
                  class="input-lg"
                  placeholder="https://example.com"
                  title="Website"
                  v-model="formatData.website"
                  type="url"
                ></el-input>
                <p class="addition">
                  ポートフォリオサイトや LinkedIn などの URL を入力してください
                </p>
              </div>
            </div>
            <div class="edit-form-item">
              <div class="item-name">最近の役職</div>
              <div class="item-body">
                <el-input
                  class="input-lg"
                  placeholder="Senior Software Engineer"
                  title="Recent Position"
                  v-model="formatData.recentPosition"
                ></el-input>
                <p class="addition">PDF ヘッダーに表示されます（任意）</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 住所カード（フリーテキスト） -->
    <div class="card" v-show="currentCardId == 'en-address'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>住所を入力</h2>
          <p>住所を入力しましょう</p>
        </div>
        <div class="card-content">
          <div class="edit-form">
            <div class="edit-form-item">
              <div class="item-name">住所</div>
              <div class="item-body">
                <el-input
                  type="textarea"
                  :autosize="{ minRows: 2, maxRows: 6 }"
                  placeholder="1-2-3 Shibuya, Shibuya-ku, Tokyo 150-0002, Japan"
                  title="住所"
                  v-model="formatData.address"
                ></el-input>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 学歴カード -->
    <div class="card" v-show="currentCardId == 'edu-history'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>学歴を入力</h2>
          <p>
            学校名・学位・期間を入力してください。ページの関係上、高校から入力するのがおすすめです
          </p>
        </div>
        <div class="card-content card-content-list">
          <div class="edit-form-list">
            <transition-group name="fade" tag="div">
              <history-item
                v-on:add-history-item="addEducationalHistory"
                v-on:delete-history-item="deleteEducationalHistory"
                v-on:move-history-item="moveEducationalHistory"
                v-for="(item, index) in formatData.educationalHistories"
                @click.native="activateHistoryItem(item.uuid)"
                :item="item"
                :length="formatData.educationalHistories.length"
                :index="index"
                :key="item.uuid"
                :id="item.uuid"
                :class="{ active: activeHistoryItem == item.uuid }"
                :ref="'EducationalHistory_' + index"
                :minYear="basicData.birthYear"
                :createDate="formatData.createDate"
                :descriptionPlaceholder="'School name, Degree'"
                :descriptionMaxLength="descriptionMaxLength"
              ></history-item>
            </transition-group>
            <div class="add-list-item">
              <el-button
                icon="el-icon-plus"
                circle
                type="success"
                :disabled="
                  formatData.educationalHistories.length >=
                  MAX_EDUCATIONAL_HISTORIES
                "
                @click="addEducationalHistory()"
              ></el-button>
              <p
                v-if="
                  formatData.educationalHistories.length >=
                  MAX_EDUCATIONAL_HISTORIES
                "
                class="limit-message"
              >
                学歴は{{ MAX_EDUCATIONAL_HISTORIES }}件まで登録できます
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- スキルカード -->
    <div class="card" v-show="currentCardId == 'skills'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>スキルを入力</h2>
          <p>アピールしたいスキルを入力してください</p>
        </div>
        <div class="card-content card-content-list">
          <div class="edit-form-list">
            <transition-group name="fade" tag="div">
              <div
                v-for="(skill, index) in formatData.skills"
                :key="'skill-' + index"
                class="edit-form-list-item skill-item"
              >
                <div class="skill-item-content">
                  <el-input
                    class="input-lg"
                    placeholder="スキル名（例：JavaScript）"
                    title="スキル"
                    v-model="formatData.skills[index]"
                    :maxlength="MAX_SKILL_LENGTH"
                    show-word-limit
                  ></el-input>
                </div>
                <div class="skill-item-actions">
                  <el-button
                    icon="el-icon-arrow-up"
                    circle
                    :disabled="!(index > 0 && formatData.skills.length > 1)"
                    @click="moveSkill(index, 'up')"
                  ></el-button>
                  <el-button
                    icon="el-icon-arrow-down"
                    circle
                    :disabled="
                      !(
                        index + 1 !== formatData.skills.length &&
                        formatData.skills.length > 1
                      )
                    "
                    @click="moveSkill(index, 'down')"
                  ></el-button>
                  <el-button icon="el-icon-delete" @click="deleteSkill(index)"
                    >削除</el-button
                  >
                </div>
              </div>
            </transition-group>
            <div class="add-list-item">
              <el-button
                icon="el-icon-plus"
                circle
                type="success"
                :disabled="formatData.skills.length >= MAX_SKILLS"
                @click="addSkill()"
              ></el-button>
              <p
                v-if="formatData.skills.length >= MAX_SKILLS"
                class="limit-message"
              >
                スキルは{{ MAX_SKILLS }}件まで登録できます
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 免許・資格カード -->
    <div class="card" v-show="currentCardId == 'license-history'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>免許・資格を入力</h2>
          <p>保有している免許や資格を入力してください</p>
        </div>
        <div class="card-content card-content-list">
          <div class="edit-form-list">
            <transition-group name="fade" tag="div">
              <history-item
                v-on:add-history-item="addLicenseHistory"
                v-on:delete-history-item="deleteLicenseHistory"
                v-on:move-history-item="moveLicenseHistory"
                v-for="(item, index) in formatData.licenseHistories"
                @click.native="activateHistoryItem(item.uuid)"
                :item="item"
                :length="formatData.licenseHistories.length"
                :index="index"
                :key="item.uuid"
                :id="item.uuid"
                :class="{ active: activeHistoryItem == item.uuid }"
                :ref="'LicenseHistory_' + index"
                :minYear="basicData.birthYear"
                :createDate="formatData.createDate"
                :descriptionPlaceholder="'免許・資格名を入力'"
                :descriptionMaxLength="MAX_LICENSE_LENGTH"
              ></history-item>
            </transition-group>
            <div class="add-list-item">
              <el-button
                icon="el-icon-plus"
                circle
                type="success"
                :disabled="
                  formatData.licenseHistories.length >= MAX_LICENSE_HISTORIES
                "
                @click="addLicenseHistory()"
              ></el-button>
              <p
                v-if="
                  formatData.licenseHistories.length >= MAX_LICENSE_HISTORIES
                "
                class="limit-message"
              >
                免許・資格は{{ MAX_LICENSE_HISTORIES }}件まで登録できます
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 職歴カード -->
    <div class="card" v-show="currentCardId == 'job-history'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>職歴を入力</h2>
          <p>会社名・役職・期間・業務内容を入力してください</p>
        </div>
        <div class="card-content card-content-list">
          <div class="edit-form-list">
            <transition-group name="fade" tag="div">
              <jobdetail-item
                v-on:add-history-item="addJobHistory"
                v-on:delete-history-item="deleteJobHistory"
                v-on:move-history-item="moveJobHistory"
                v-on:add-project-history-item="addProjectHistory"
                v-on:delete-history-item-detail="deleteHistoryDetail"
                v-on:move-history-item-detail="moveJobHistoryDetail"
                v-for="(item, index) in formatData.jobHistories"
                @click.native="activateHistoryItem(item.uuid)"
                :item="item"
                :length="formatData.jobHistories.length"
                :index="index"
                :key="item.uuid"
                :id="item.uuid"
                :class="{ active: activeHistoryItem == item.uuid }"
                :ref="'JobHistory_' + index"
                :minYear="basicData.birthYear"
                :createDate="formatData.createDate"
                :activeHistoryItem="activeHistoryItem"
                :descriptionPlaceholder="'会社名を入力'"
                :descriptionMaxLength="descriptionMaxLength"
                :hideCompanyDetails="true"
              >
              </jobdetail-item>
            </transition-group>
            <div class="add-list-item">
              <el-button
                icon="el-icon-plus"
                circle
                type="success"
                @click="addJobHistory()"
              ></el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 自己PRカード -->
    <div class="card" v-show="currentCardId == 'pr-message'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>自己PRを入力</h2>
          <p>英文レジュメでは Profile としてトップに表示されます</p>
        </div>
        <div class="card-content">
          <english-profile-ai-card
            :current-value="formatData.prMessage"
            @adopt-profile="handleAdoptProfile"
          ></english-profile-ai-card>
          <el-input
            type="textarea"
            v-model="formatData.prMessage"
            :autosize="{ minRows: 6, maxRows: 15 }"
            :maxlength="MAX_PR_MESSAGE_LENGTH"
            show-word-limit
            placeholder="Experienced software engineer with ..."
          ></el-input>
        </div>
      </div>
    </div>

    <!-- 作成日カード -->
    <div class="card" v-show="currentCardId == 'create-date'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>レジュメの作成日を入力</h2>
          <p>実際に書類を持参する日、または郵送する日を選びましょう</p>
        </div>
        <div class="card-content">
          <div class="edit-form-list">
            <div class="create-date-action">
              <div class="action-item">
                <el-button
                  class="button-block"
                  :disabled="
                    $formLib.dateWithoutTime(formatData.createDate) ==
                    $formLib.dateWithoutTime($formLib.getToday())
                  "
                  @click="formatData.createDate = $formLib.getToday()"
                  >今日の日付を入力</el-button
                >
              </div>
              <div class="action-item">
                <el-button
                  class="button-block"
                  :disabled="
                    $formLib.dateWithoutTime(formatData.createDate) ==
                    $formLib.dateWithoutTime($formLib.getTommorow())
                  "
                  @click="formatData.createDate = $formLib.getTommorow()"
                  >明日の日付を入力</el-button
                >
              </div>
              <div class="action-item">
                <el-date-picker
                  v-model="formatData.createDate"
                  type="date"
                  :clearable="false"
                  placeholder="選択してください"
                  :format="'yyyy年M月d日'"
                  ref="create_date"
                >
                </el-date-picker>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 確認カード -->
    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>作成した英語レジュメを確認</h2>
          <p>
            修正する場合は、プレビューの修正欄をクリックまたはステップを戻ることで修正することができます
          </p>
        </div>
      </div>
    </div>

    <!-- 完了カード -->
    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <download-item
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
      ></download-item>
    </div>
  </div>
</template>

<script>
import HistoryItem from './RirekishoParts/HistoryItem'
import HistoryItemJob from './ShokurekishoParts/HistoryItemJob'
import DownloadItem from './CommonParts/DownloadItem'
import EnglishProfileAiCard from '@/components/yagiprime/EnglishProfileAiCard'
import LocalDataMixin from '@/mixins/local-data-mixin'

const uuidv4 = function () {
  const cryptoObj = window.crypto || window.msCrypto
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (cryptoObj.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  )
}

const HistoryData = function (columns) {
  this.year = Number(columns.year) || ''
  this.month = Number(columns.month) || ''
  this.description = columns.description || ''
  this.uuid = uuidv4()
}

const JobHistoryData = function (columns) {
  this.companyName = columns.companyName || ''
  this.startYear = Number(columns.startYear) || ''
  this.startMonth = Number(columns.startMonth) || ''
  this.endYear = Number(columns.endYear) || ''
  this.endMonth = Number(columns.endMonth) || ''
  this.isContinuing = columns.isContinuing || false
  this.businessArea = columns.businessArea || ''
  this.projects = columns.projects || []
  this.uuid = uuidv4()
}

const ProjectHistoryData = function (columns) {
  this.projectTitle = columns.projectTitle || ''
  this.startYear = Number(columns.startYear) || ''
  this.startMonth = Number(columns.startMonth) || ''
  this.endYear = Number(columns.endYear) || ''
  this.endMonth = Number(columns.endMonth) || ''
  this.contentsOfProject = columns.contentsOfProject || ''
  this.envAndScale = columns.envAndScale || ''
  this.position = columns.position || ''
  this.uuid = uuidv4()
}

export default {
  name: 'EnglishResumeEdit',
  props: {
    basicData: {
      type: Object,
      require: true
    },
    formatId: {
      type: String
    },
    currentCardId: {
      type: String
    },
    exampleSentences: {
      type: Object
    }
  },
  mixins: [LocalDataMixin],
  components: {
    'history-item': HistoryItem,
    'jobdetail-item': HistoryItemJob,
    'download-item': DownloadItem,
    'english-profile-ai-card': EnglishProfileAiCard
  },
  data: function () {
    return {
      saveName: 'english_resume',
      saveOtherName: 'rirekisho',
      formatData: {
        createDate: null,
        website: '',
        recentPosition: '',
        address: '',
        educationalHistories: [],
        skills: [],
        licenseHistories: [],
        jobHistories: [],
        prMessage: ''
      },
      activeHistoryItem: '',
      descriptionMaxLength: 80,
      MAX_EDUCATIONAL_HISTORIES: 4,
      MAX_SKILLS: 10,
      MAX_SKILL_LENGTH: 30,
      MAX_LICENSE_HISTORIES: 10,
      MAX_LICENSE_LENGTH: 30,
      MAX_PR_MESSAGE_LENGTH: 350,
      mailPdfEnabled: true,
      downloadPdfEnabled: true,
      isSendYagiPostEnabled: true,
      isSendYagiEmailEnabled: true,
      downloadActiveCollapses: ['postcard']
    }
  },
  computed: {
    getMode: function () {
      return process.env.VUE_APP_MODE
    }
  },
  methods: {
    // 学歴
    addEducationalHistory: function (index, columns) {
      const historyList = this.formatData.educationalHistories
      if (historyList.length >= this.MAX_EDUCATIONAL_HISTORIES) return
      if (!columns) columns = {}
      const newHistoryItem = new HistoryData(columns)
      if (typeof index === 'number') {
        historyList.splice(index, 0, newHistoryItem)
      } else {
        historyList.push(newHistoryItem)
      }
    },
    deleteEducationalHistory: function (index) {
      this.formatData.educationalHistories.splice(index, 1)
    },
    moveEducationalHistory: function (params) {
      this.moveHistory(this.formatData.educationalHistories, params)
    },
    // 免許・資格
    addLicenseHistory: function (index, columns) {
      const historyList = this.formatData.licenseHistories
      if (historyList.length >= this.MAX_LICENSE_HISTORIES) return
      if (!columns) columns = {}
      const newHistoryItem = new HistoryData(columns)
      if (typeof index === 'number') {
        historyList.splice(index, 0, newHistoryItem)
      } else {
        historyList.push(newHistoryItem)
      }
    },
    deleteLicenseHistory: function (index) {
      this.formatData.licenseHistories.splice(index, 1)
    },
    moveLicenseHistory: function (params) {
      this.moveHistory(this.formatData.licenseHistories, params)
    },
    // スキル
    addSkill: function () {
      if (this.formatData.skills.length >= this.MAX_SKILLS) return
      this.formatData.skills.push('')
    },
    deleteSkill: function (index) {
      this.formatData.skills.splice(index, 1)
    },
    moveSkill: function (index, to) {
      const list = this.formatData.skills
      if (to === 'up' && index > 0) {
        list.splice(index - 1, 2, list[index], list[index - 1])
      } else if (to === 'down' && index + 1 < list.length) {
        list.splice(index, 2, list[index + 1], list[index])
      }
    },
    // 職歴
    addJobHistory: function (index, columns) {
      const historyList = this.formatData.jobHistories
      if (!columns) columns = {}
      const newHistoryItem = new JobHistoryData(columns)
      if (typeof index === 'number') {
        historyList.splice(index, 0, newHistoryItem)
      } else {
        historyList.push(newHistoryItem)
      }
    },
    deleteJobHistory: function (index) {
      this.formatData.jobHistories.splice(index, 1)
    },
    moveJobHistory: function (params) {
      this.moveHistory(this.formatData.jobHistories, params)
    },
    addProjectHistory: function (index, columns) {
      const targetItem = this.formatData.jobHistories[index]
      if (!columns) columns = {}
      const newHistoryItem = new ProjectHistoryData(columns)
      targetItem.projects.push(newHistoryItem)
    },
    deleteHistoryDetail: function (index, index2) {
      this.formatData.jobHistories[index].projects.splice(index2, 1)
    },
    moveJobHistoryDetail: function (params, parentIndex) {
      this.moveHistory(
        this.formatData.jobHistories[parentIndex].projects,
        params
      )
    },
    // 共通並び替え
    moveHistory: function (historyList, params) {
      const fromIndex = params.index
      const to = params.to
      if (fromIndex === 0 && to === 'up') {
        return
      }
      if (fromIndex + 1 === historyList.length && to === 'down') {
        return
      }
      if (to === 'up') {
        historyList.splice(
          fromIndex - 1,
          2,
          historyList[fromIndex],
          historyList[fromIndex - 1]
        )
      } else if (to === 'down') {
        historyList.splice(
          fromIndex,
          2,
          historyList[fromIndex + 1],
          historyList[fromIndex]
        )
      }
    },
    activateHistoryItem: function (id) {
      this.activeHistoryItem = id
    },
    deactivateHistoryItem: function () {
      this.activeHistoryItem = ''
    },
    dataChanged: function () {
      this.mailPdfEnabled = true
      this.downloadPdfEnabled = true
    },
    handleAdoptProfile: function (text) {
      this.formatData.prMessage = text
    }
  },
  beforeMount: function () {
    if (this.formatData.createDate === null) {
      this.formatData.createDate = this.$formLib.getToday()
    }
    this.$eventHub.$on('deactivate-history-item', this.deactivateHistoryItem)
    this.$eventHub.$on('basic-data-changed', this.dataChanged)
    this.$eventHub.$on('format-data-changed', this.dataChanged)
  },
  destroyed: function () {
    this.$eventHub.$off('deactivate-history-item', this.deactivateHistoryItem)
    this.$eventHub.$off('basic-data-changed', this.dataChanged)
    this.$eventHub.$off('format-data-changed', this.dataChanged)
  }
}
</script>

<style scoped lang="scss">
.addition {
  font-size: 0.75rem;
  margin: 0.5rem 0;
  color: #666;
}

.skill-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  margin-bottom: 10px;
}

.skill-item-content {
  flex: 1;
}

.skill-item-actions {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

.limit-message {
  margin: 8px 0 0;
  font-size: 0.78rem;
  color: #999;
  text-align: center;
}
</style>
